import { motion } from "framer-motion";
import { Upload, MessageCircle, Eye, FileText, Plus, DollarSign } from "lucide-react";

const ACTIONS = {
  borrower: [
    { key: "upload", label: "Upload Doc", icon: Upload, color: "#059669" },
    { key: "message", label: "Message Team", icon: MessageCircle, color: "#2563eb" },
    { key: "status", label: "Check Status", icon: Eye, color: "#D4AF37" },
    { key: "new_deal", label: "New Deal", icon: Plus, color: "#7c3aed" },
  ],
  lender: [
    { key: "view_deals", label: "Browse Deals", icon: Eye, color: "#D4AF37" },
    { key: "term_sheet", label: "Term Sheet", icon: FileText, color: "#059669" },
    { key: "loan_box", label: "Loan Box", icon: DollarSign, color: "#2563eb" },
    { key: "messages", label: "Messages", icon: MessageCircle, color: "#7c3aed" },
  ],
  investor: [
    { key: "opportunities", label: "Browse", icon: Eye, color: "#D4AF37" },
    { key: "invest", label: "Invest", icon: DollarSign, color: "#059669" },
    { key: "portfolio", label: "Portfolio", icon: FileText, color: "#2563eb" },
    { key: "messages", label: "Messages", icon: MessageCircle, color: "#7c3aed" },
  ],
};

export function QuickActionsBar({ role = "borrower", onAction, className = "" }) {
  const actions = ACTIONS[role] || ACTIONS.borrower;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className={`flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide ${className}`}
      data-testid="quick-actions-bar"
    >
      {actions.map((action, i) => (
        <motion.button
          key={action.key}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: i * 0.06 }}
          onClick={() => onAction?.(action.key)}
          className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-full border border-[#EDE8DC] bg-white hover:border-emerald-200 hover:shadow-[0_2px_10px_rgba(6,95,70,0.06)] transition-all duration-200 active:scale-95 group"
          data-testid={`quick-action-${action.key}`}
        >
          <action.icon className="h-3.5 w-3.5 transition-colors group-hover:scale-110 transition-transform" style={{ color: action.color }} strokeWidth={1.8} />
          <span className="text-xs font-medium text-[#555] group-hover:text-[#222] transition-colors whitespace-nowrap">{action.label}</span>
        </motion.button>
      ))}
    </motion.div>
  );
}

export function FilterPills({ filters = [], active, onSelect, className = "" }) {
  return (
    <div className={`flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide ${className}`} data-testid="filter-pills">
      {filters.map((f, i) => {
        const isActive = active === f.key;
        return (
          <motion.button
            key={f.key}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            onClick={() => onSelect(f.key)}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 border ${
              isActive
                ? "bg-emerald-50 border-emerald-200 text-emerald-700 shadow-sm"
                : "bg-white border-[#EDE8DC] text-[#888] hover:border-emerald-100 hover:text-[#555]"
            }`}
            data-testid={`filter-pill-${f.key}`}
          >
            {f.count !== undefined ? `${f.label} (${f.count})` : f.label}
          </motion.button>
        );
      })}
    </div>
  );
}

export function LivePulse({ count, label = "active now" }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="live-pulse">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      <span className="text-[10px] text-emerald-600 font-medium">
        {count !== undefined ? `${count} ${label}` : label}
      </span>
    </div>
  );
}

export function SocialProofBadge({ text, variant = "interest" }) {
  const styles = {
    interest: "bg-blue-50 border-blue-100 text-blue-600",
    funded: "bg-emerald-50 border-emerald-100 text-emerald-700",
    urgency: "bg-amber-50 border-amber-100 text-amber-700",
    hot: "bg-red-50 border-red-100 text-red-600",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[variant] || styles.interest}`} data-testid="social-proof-badge">
      {variant === "interest" && <Eye className="h-2.5 w-2.5" />}
      {variant === "funded" && <DollarSign className="h-2.5 w-2.5" />}
      {variant === "urgency" && <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500" /></span>}
      {text}
    </span>
  );
}
